import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Receipt, Loader2, ChevronLeft, ChevronRight, Download, FileSpreadsheet } from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';
import { API } from '../../../lib/api';
import { fmtINR, formatErr } from '../../../components/admin/billing/shared';

const currentYM = () => {
  const d = new Date();
  return { y: d.getFullYear(), m: d.getMonth() + 1 };
};

const shiftMonth = ({ y, m }, delta) => {
  const d = new Date(Date.UTC(y, m - 1 + delta, 1));
  return { y: d.getUTCFullYear(), m: d.getUTCMonth() + 1 };
};

const monthLabel = ({ y, m }) => {
  const d = new Date(Date.UTC(y, m - 1, 1));
  return d.toLocaleString('en-IN', { month: 'long', year: 'numeric', timeZone: 'UTC' });
};

const Gstr2Report = () => {
  const { authHeader } = useAuth();
  const [{ y, m }, setYm] = useState(currentYM());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [downloadingXlsx, setDownloadingXlsx] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true); setErr('');
    try {
      const { data } = await axios.get(`${API}/gst/reports/gstr2`, {
        headers: authHeader(),
        params: { year: y, month: m },
      });
      setData(data);
    } catch (e) {
      setErr(formatErr(e));
    } finally {
      setLoading(false);
    }
  }, [authHeader, y, m]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const rows = data?.rows || [];
  const suppliers = data?.by_supplier || [];
  const totals = data?.totals || {
    invoice_value: 0, taxable_value: 0, cgst: 0, sgst: 0, igst: 0, cess: 0, total_itc: 0,
  };

  const downloadXlsx = async () => {
    setDownloadingXlsx(true);
    try {
      const resp = await axios.get(`${API}/gst/reports/gstr2/excel`, {
        headers: authHeader(),
        params: { year: y, month: m },
        responseType: 'blob',
      });
      const url = URL.createObjectURL(new Blob([resp.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `GSTR2_ITC_${y}_${String(m).padStart(2, '0')}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setErr(formatErr(e));
    } finally {
      setDownloadingXlsx(false);
    }
  };

  const csv = useMemo(() => {
    if (!rows.length) return '';
    const header = [
      'Date', 'GSTIN of Supplier', 'Supplier Name', 'Invoice Number',
      'Category', 'Invoice Value', 'Taxable Value', 'Effective Rate %',
      'CGST', 'SGST', 'IGST', 'Cess', 'Total ITC',
    ];
    const body = rows.map((r) => [
      r.date, r.supplier_gstin || '', r.paid_to || '', r.reference || '',
      r.category || '', r.invoice_value, r.taxable_value,
      r.effective_rate ?? '', r.cgst_amount, r.sgst_amount,
      r.igst_amount, r.cess_amount, r.total_itc,
    ]);
    return [header, ...body]
      .map((row) => row.map((c) => `"${String(c ?? '').replace(/"/g, '""')}"`).join(','))
      .join('\n');
  }, [rows]);

  const downloadCsv = () => {
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `gstr2_itc_${y}_${String(m).padStart(2, '0')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="p-4 sm:p-6 bg-slate-50 min-h-full space-y-4" data-testid="gstr2-page">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Receipt className="text-teal-600" size={22} />
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-slate-900">GSTR-2 · Input Tax Credit</h1>
            <p className="text-xs text-slate-500">
              ITC-eligible purchase invoices from GST-tagged expenses. Feeds GSTR-3B (4A).
            </p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Link
            to="/admin/reports"
            className="text-xs bg-slate-100 hover:bg-slate-200 border border-slate-300 text-slate-800 px-3 py-1.5 rounded-lg"
            data-testid="back-to-reports"
          >
            ← Reports hub
          </Link>
          <button
            type="button"
            onClick={downloadCsv}
            disabled={!rows.length}
            className="text-xs bg-slate-100 hover:bg-slate-200 disabled:opacity-50 border border-slate-300 text-slate-800 px-3 py-1.5 rounded-lg inline-flex items-center gap-1.5"
            data-testid="download-csv"
          >
            <Download size={13} /> CSV
          </button>
          <button
            type="button"
            onClick={downloadXlsx}
            disabled={!rows.length || downloadingXlsx}
            className="text-xs bg-teal-500 hover:bg-teal-600 disabled:bg-teal-300 text-white font-semibold px-3 py-1.5 rounded-lg inline-flex items-center gap-1.5"
            data-testid="download-xlsx"
          >
            {downloadingXlsx ? <Loader2 className="animate-spin" size={13} /> : <FileSpreadsheet size={13} />}
            GSTN Excel
          </button>
        </div>
      </div>

      {/* Month picker */}
      <div className="bg-white rounded-xl border border-slate-200 p-3 flex items-center gap-2">
        <button
          type="button"
          onClick={() => setYm((p) => shiftMonth(p, -1))}
          className="p-1.5 rounded hover:bg-slate-100 text-slate-700"
          data-testid="prev-month"
        >
          <ChevronLeft size={16} />
        </button>
        <div className="min-w-[160px] text-center font-semibold text-slate-800" data-testid="month-label">
          {monthLabel({ y, m })}
        </div>
        <button
          type="button"
          onClick={() => setYm((p) => shiftMonth(p, 1))}
          className="p-1.5 rounded hover:bg-slate-100 text-slate-700"
          data-testid="next-month"
        >
          <ChevronRight size={16} />
        </button>
        <button
          type="button"
          onClick={() => setYm(currentYM())}
          className="ml-2 text-xs px-2 py-1 rounded bg-slate-100 hover:bg-slate-200 text-slate-700"
          data-testid="this-month"
        >
          This month
        </button>
        <span className="ml-auto text-xs text-slate-500" data-testid="rows-count">
          {loading ? '…' : `${rows.length} bill${rows.length === 1 ? '' : 's'} · ${suppliers.length} supplier${suppliers.length === 1 ? '' : 's'}`}
        </span>
      </div>

      {/* Totals summary */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3">
        <SummaryCard label="Invoice value" value={totals.invoice_value} accent="slate" />
        <SummaryCard label="Taxable" value={totals.taxable_value} accent="slate" />
        <SummaryCard label="CGST" value={totals.cgst} accent="sky" />
        <SummaryCard label="SGST" value={totals.sgst} accent="sky" />
        <SummaryCard label="IGST" value={totals.igst} accent="indigo" />
        <SummaryCard label="Cess" value={totals.cess} accent="slate" />
        <SummaryCard label="Total ITC" value={totals.total_itc} accent="teal" bold />
      </div>

      {err && (
        <div className="border border-red-200 bg-red-50 text-red-700 text-sm rounded-lg px-3 py-2" data-testid="err">
          {err}
        </div>
      )}

      {/* By supplier */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="px-4 py-2 border-b border-slate-200 bg-slate-50 flex items-center justify-between">
          <h2 className="font-semibold text-slate-800 text-sm">By supplier</h2>
          <span className="text-xs text-slate-500">Grouped by GSTIN</span>
        </div>
        {loading ? (
          <div className="p-6 text-center text-slate-400 flex items-center justify-center gap-2">
            <Loader2 className="animate-spin" size={16} /> Loading…
          </div>
        ) : suppliers.length === 0 ? (
          <div className="p-6 text-center text-slate-400 text-sm">
            No GST-tagged purchases this month.
            <p className="text-xs mt-1">Tick “This is a GST invoice” in the Expenses editor to include a bill here.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm" data-testid="suppliers-table">
              <thead className="bg-slate-50 text-slate-500 text-xs">
                <tr>
                  <th className="text-left px-3 py-2">Supplier / GSTIN</th>
                  <th className="text-right px-3 py-2">Bills</th>
                  <th className="text-right px-3 py-2">Taxable</th>
                  <th className="text-right px-3 py-2">CGST</th>
                  <th className="text-right px-3 py-2">SGST</th>
                  <th className="text-right px-3 py-2">IGST</th>
                  <th className="text-right px-3 py-2">Cess</th>
                  <th className="text-right px-3 py-2 bg-teal-50 text-teal-700">Total ITC</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {suppliers.map((s) => (
                  <tr key={s.gstin} data-testid={`sup-${s.gstin}`}>
                    <td className="px-3 py-2">
                      <div className="text-slate-800">{s.name || '—'}</div>
                      <div className="text-[11px] font-mono text-slate-500">{s.gstin}</div>
                    </td>
                    <td className="px-3 py-2 text-right">{s.bills}</td>
                    <td className="px-3 py-2 text-right">₹ {fmtINR(s.taxable_value)}</td>
                    <td className="px-3 py-2 text-right">₹ {fmtINR(s.cgst)}</td>
                    <td className="px-3 py-2 text-right">₹ {fmtINR(s.sgst)}</td>
                    <td className="px-3 py-2 text-right">₹ {fmtINR(s.igst)}</td>
                    <td className="px-3 py-2 text-right">₹ {fmtINR(s.cess)}</td>
                    <td className="px-3 py-2 text-right font-semibold text-teal-700 bg-teal-50/50">
                      ₹ {fmtINR(s.total_itc)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* All bills */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="px-4 py-2 border-b border-slate-200 bg-slate-50">
          <h2 className="font-semibold text-slate-800 text-sm">All ITC-eligible bills</h2>
        </div>
        {!loading && rows.length > 0 && (
          <div className="overflow-x-auto">
            <table className="min-w-full text-xs" data-testid="bills-table">
              <thead className="bg-slate-50 text-slate-500">
                <tr>
                  <th className="text-left px-3 py-2">Date</th>
                  <th className="text-left px-3 py-2">GSTIN</th>
                  <th className="text-left px-3 py-2">Supplier</th>
                  <th className="text-left px-3 py-2">Bill #</th>
                  <th className="text-right px-3 py-2">Value</th>
                  <th className="text-right px-3 py-2">Taxable</th>
                  <th className="text-right px-3 py-2">Rate</th>
                  <th className="text-right px-3 py-2">CGST</th>
                  <th className="text-right px-3 py-2">SGST</th>
                  <th className="text-right px-3 py-2">IGST</th>
                  <th className="text-right px-3 py-2 bg-teal-50 text-teal-700">ITC</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.map((r) => (
                  <tr key={r.id} data-testid={`bill-${r.id}`}>
                    <td className="px-3 py-1.5 text-slate-600">{r.date}</td>
                    <td className="px-3 py-1.5 font-mono text-slate-600">{r.supplier_gstin}</td>
                    <td className="px-3 py-1.5 text-slate-700">{r.paid_to || '—'}</td>
                    <td className="px-3 py-1.5 text-slate-600">{r.reference || '—'}</td>
                    <td className="px-3 py-1.5 text-right">₹ {fmtINR(r.invoice_value)}</td>
                    <td className="px-3 py-1.5 text-right">₹ {fmtINR(r.taxable_value)}</td>
                    <td className="px-3 py-1.5 text-right text-slate-500">
                      {r.effective_rate != null ? `${r.effective_rate}%` : '—'}
                    </td>
                    <td className="px-3 py-1.5 text-right">₹ {fmtINR(r.cgst_amount)}</td>
                    <td className="px-3 py-1.5 text-right">₹ {fmtINR(r.sgst_amount)}</td>
                    <td className="px-3 py-1.5 text-right">₹ {fmtINR(r.igst_amount)}</td>
                    <td className="px-3 py-1.5 text-right font-semibold text-teal-700 bg-teal-50/50">
                      ₹ {fmtINR(r.total_itc)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

const SummaryCard = ({ label, value, accent = 'slate', bold = false }) => {
  const color = {
    slate: 'text-slate-800 border-slate-200',
    sky: 'text-sky-700 border-sky-200 bg-sky-50/60',
    indigo: 'text-indigo-700 border-indigo-200 bg-indigo-50/60',
    teal: 'text-teal-700 border-teal-300 bg-teal-50',
  }[accent];
  return (
    <div className={`border rounded-lg p-3 ${color}`}>
      <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className={`mt-0.5 ${bold ? 'text-lg font-bold' : 'text-base font-semibold'}`}>
        ₹ {fmtINR(value)}
      </div>
    </div>
  );
};

export default Gstr2Report;
